import React, { useState } from "react";
import { Button } from "../../components/template";

/**
 * Scoring:
 * Balanced answer = 1 point
 * Other options = 0 points
 * Max Score = 15
 */

// Index of the most balanced answer for each question
const correctAnswers = [
  0, // Q1
  0, // Q2
  0, // Q3
  0, // Q4
  0, // Q5
  0, // Q6
  0, // Q7
  0, // Q8
  0, // Q9
  0, // Q10
  0, // Q11
  0, // Q12
  0, // Q13
  0, // Q14
  0, // Q15
];

const quizData = [
  {
    question:
      "When people talk about “good sleep,” which situation usually feels the most refreshing the next day?",
    options: [
      "Sleeping earlier at night and waking up without an alarm",
      "Sleeping late but completing 7–8 hours",
      "Sleeping whenever possible, even if timings change daily",
    ],
  },
  {
    question: "Someone who sleeps late most nights often notices…",
    options: [
      "Feeling tired despite sleeping enough hours",
      "Feeling energetic as long as total sleep is enough",
      "No noticeable difference over time",
    ],
  },
  {
    question:
      "If a person sleeps for 8 hours but still wakes up exhausted, a common reason could be…",
    options: [
      "Sleep happening at odd or inconsistent times",
      "Not sleeping long enough",
      "Lack of physical movement during the day",
    ],
  },
  {
    question:
      "When life feels continuously stressful, the body often shows signs through…",
    options: [
      "Sleep issues, digestion trouble, or mood changes",
      "Only emotional reactions",
      "Sudden illness without warning",
    ],
  },
  {
    question:
      "Which situation best describes how stress usually builds up in real life?",
    options: [
      "Many small pressures adding up over time",
      "One big stressful event only",
      "Stress appears without any reason",
    ],
  },
  {
    question:
      "People who are “always busy” but rarely rest often experience…",
    options: [
      "Fatigue, irritability, or low motivation",
      "Constant high energy",
      "Better focus and calm",
    ],
  },
  {
    question:
      "Which habit most often helps people fall asleep more easily at night?",
    options: [
      "Slowing down and reducing stimulation before bed",
      "Staying active and distracted till the last moment",
      "Eating a heavy meal to feel sleepy",
    ],
  },
  {
    question:
      "Waking up at very different times each day usually leads to…",
    options: [
      "Feeling groggy or unsettled",
      "Better adaptability",
      "No effect as long as sleep hours are enough",
    ],
  },
  {
    question:
      "When people try to “catch up” on sleep during the day, it usually…",
    options: [
      "Helps a little but doesn’t fully replace night sleep",
      "Completely fixes sleep loss",
      "Works better than sleeping at night",
    ],
  },
  {
    question:
      "Which statement best reflects how stress works in the body?",
    options: [
      "Short-term stress can help, long-term stress drains",
      "All stress is harmful",
      "Stress only affects emotions, not the body",
    ],
  },
  {
    question:
      "People with ongoing sleep or hormonal complaints often also notice issues with…",
    options: [
      "Digestion, mood, or energy",
      "Only weight",
      "Only mental focus",
    ],
  },
  {
    question:
      "Which daily pattern usually feels the most supportive for overall balance?",
    options: [
      "Similar sleep, meal, and wake times most days",
      "Flexible routines that change daily",
      "No routine, just going with the flow",
    ],
  },
  {
    question:
      "Using phones or laptops right before sleeping often leads to…",
    options: [
      "Difficulty switching off mentally",
      "Faster sleep because of distraction",
      "Deeper and longer sleep",
    ],
  },
  {
    question:
      "When people talk about “hormonal imbalance,” it most often connects to…",
    options: [
      "Lifestyle, stress, sleep, and routine together",
      "One single hormone problem only",
      "Something that appears suddenly without warning",
    ],
  },
  {
    question:
      "A healthy long-term approach to sleep and stress usually looks like…",
    options: [
      "Consistent habits with room for flexibility",
      "Strict rules followed perfectly",
      "Ignoring the body until symptoms appear",
    ],
  },
];

/* -------------------- INTRO PAGE -------------------- */

const IntroPage = ({ onStart }) => (
  <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-6 py-10">
    <div
      className="
        relative
        overflow-hidden
        rounded-[34px]
        max-w-5xl
        w-full
        h-[620px]
        shadow-[0_30px_80px_rgba(20,10,0,.12)]
      "
    >
      <img
        src="/assets/knowyourself/quiz/sleepstress.jpg"
        alt="Sleep Stress Assessment"
        className="
          w-full
          h-full
          object-cover
          brightness-[.84]
          saturate-[.9]
        "
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent" />

      <div className="absolute inset-x-0 bottom-0 p-6">
        <div
          className="
            rounded-[28px]
            bg-white/10
            backdrop-blur-2xl
            border
            border-white/10
            p-8
          "
        >
          <div
            className="
              inline-flex
              px-4
              py-2
              rounded-full
              bg-white/10
              border
              border-white/10
              text-white
              text-xs
              tracking-[.08em]
              mb-5
            "
          >
            KNOWLEDGE ASSESSMENT
          </div>

          <h1
            className="
              text-[38px]
              md:text-[56px]
              leading-[1.05]
              text-white
              mb-5
            "
          >
            Sleep, Stress &
            <br />
            Body Awareness
          </h1>

          <p
            className="
              text-white/75
              text-[15px]
              leading-8
              mb-8
              max-w-3xl
            "
          >
            Discover how well you understand the
            relationship between sleep, stress,
            hormones, recovery, energy, and overall wellbeing.
          </p>

          <Button
            variant="primary"
            text="Start Assessment"
            onClick={onStart}
          />
        </div>
      </div>
    </div>
  </div>
);

/* -------------------- QUIZ PAGE -------------------- */

const QuizPage = ({ onComplete }) => {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState(Array(quizData.length).fill(null));
  const [error, setError] = useState("");

  const handleSelect = (index) => {
    const updated = [...answers];
    updated[current] = index;
    setAnswers(updated);
    setError("");
  };

  const handleNext = () => {
    if (answers[current] === null) {
      setError("Please select an option to proceed.");
      return;
    }

    if (current < quizData.length - 1) {
      setCurrent(current + 1);
    } else {
      onComplete(answers);
    }
  };

  const handlePrev = () => {
    setError("");
    if (current > 0) setCurrent(current - 1);
  };

  const progress = ((current + 1) / quizData.length) * 100;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10">
      <div className="w-full max-w-2xl bg-white p-8 rounded-2xl shadow-lg text-center">
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">
            Question {current + 1} of {quizData.length}
          </p>
          <div className="w-full bg-gray-200 h-2 rounded-full">
            <div
              className="bg-primary h-2 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <p className="text-[#578d48] font-bold text-xl mb-4">
          {quizData[current].question}
        </p>

        <div className="space-y-3">
          {quizData[current].options.map((opt, i) => (
            <label
              key={i}
              className={`flex items-center justify-center p-3 rounded-lg cursor-pointer transition
                ${
                  answers[current] === i
                    ? "bg-primary text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
            >
              <input
                type="radio"
                checked={answers[current] === i}
                onChange={() => handleSelect(i)}
                className="hidden"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>

        {error && <p className="mt-4 text-red-500 text-sm">{error}</p>}
      </div>

      <div className="flex mt-6 space-x-4">
        {current > 0 && (
          <Button text="Previous" variant="whitish" onClick={handlePrev} />
        )}
        <Button
          text={current === quizData.length - 1 ? "Submit" : "Next"}
          variant="primary"
          onClick={handleNext}
        />
      </div>
    </div>
  );
};

/* -------------------- RESULT PAGE -------------------- */

const ResultPage = ({ answers, onRetake }) => {
  const totalScore = answers.reduce(
    (sum, ans, i) => sum + (ans === correctAnswers[i] ? 1 : 0),
    0
  );

  let title = "";
  let message = "";

  if (totalScore >= 12) {
    title = "🌙 Strong Body Awareness";

    message =
      "You have a clear understanding of how sleep, stress, recovery, and daily habits influence your health. You recognise patterns and understand that wellbeing is built through consistency rather than quick fixes.";
  } else if (totalScore >= 7) {
    title = "🌱 Partial Awareness";

    message =
      "You understand several important concepts but may underestimate the impact of routine, sleep timing, recovery, and stress management. Small improvements in awareness can make a meaningful difference.";
  } else {
    title = "🌧️ Awareness Needs Strengthening";

    message =
      "You may still rely on common myths or oversimplified ideas about sleep and stress. Building a deeper understanding of recovery, routine, and body signals can help create lasting wellbeing.";
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-6 py-10">
      <div
        className="
          relative
          overflow-hidden
          rounded-[34px]
          max-w-5xl
          w-full
          h-[620px]
          shadow-[0_30px_80px_rgba(20,10,0,.12)]
        "
      >
        <img
          src="/assets/knowyourself/quiz/result.jpg"
          alt="Assessment Result"
          className="
            w-full
            h-full
            object-cover
            brightness-[.84]
            saturate-[.9]
          "
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent" />

        <div className="absolute inset-x-0 bottom-0 p-6">
          <div
            className="
              rounded-[28px]
              bg-white/10
              backdrop-blur-2xl
              border
              border-white/10
              p-8
            "
          >
            <div
              className="
                inline-flex
                px-4
                py-2
                rounded-full
                bg-white/10
                border
                border-white/10
                text-white
                text-xs
                tracking-[.08em]
                mb-5
              "
            >
              YOUR RESULT
            </div>

            <h2
              className="
                text-[34px]
                md:text-[52px]
                leading-[1.05]
                text-white
                mb-4
              "
            >
              {title}
            </h2>

            <p
              className="
                text-white/75
                text-[15px]
                leading-8
                mb-4
              "
            >
              Knowledge Score: {totalScore} / 15
            </p>

            <p
              className="
                text-white/75
                text-[15px]
                leading-8
                mb-8
                max-w-3xl
              "
            >
              {message}
            </p>

            <Button
              variant="primary"
              text="Retake Assessment"
              onClick={onRetake}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* -------------------- MAIN COMPONENT -------------------- */

const SleepStressAwarenessQuiz = () => {
  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [answers, setAnswers] = useState(null);

  return (
    <>
      {!started ? (
        <IntroPage onStart={() => setStarted(true)} />
      ) : completed ? (
        <ResultPage
          answers={answers}
          onRetake={() => {
            setStarted(false);
            setCompleted(false);
            setAnswers(null);
          }}
        />
      ) : (
        <QuizPage
          onComplete={(ans) => {
            setAnswers(ans);
            setCompleted(true);
          }}
        />
      )}
    </>
  );
};

export default SleepStressAwarenessQuiz;
